import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Settings, Trash2, Check, Play, Pause, Square, Activity, AlertCircle, RefreshCw, DollarSign, TrendingUp, Eye } from 'lucide-react';
import { Strategy } from './types';
import { getFeatureAttributions } from '@/services/explainability';
import { FeatureImportance } from '@/components/ui/feature-importance';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { deploymentService, DeploymentStatus } from '@/services/deploymentService';

interface StrategyCardProps {
  strategy: Strategy;
  isExpanded: boolean;
  onResearch: (strategyId: string) => void;
  onDeleteClick: (strategyId: string) => void;
  onStrategyClick: (strategy: Strategy) => void;
  selectedStrategy: string;
}

export const StrategyCard: React.FC<StrategyCardProps> = ({
  strategy,
  isExpanded,
  onResearch,
  onDeleteClick,
  onStrategyClick,
  selectedStrategy,
}) => {
  const { toast } = useToast();
  
  // Live deployment status tracking
  const [deploymentStatus, setDeploymentStatus] = useState<DeploymentStatus | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [lastStatusUpdate, setLastStatusUpdate] = useState<Date | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Explainability state
  const [showExplain, setShowExplain] = useState(false);
  const [featureData, setFeatureData] = useState<{ feature: string; importance: number }[]>([]);
  const [loadingExplain, setLoadingExplain] = useState(false);

  // Load deployment status for deployed strategies and merge execution timing
  const loadDeploymentStatus = async (strategyId: string) => {
    try {
      setIsLoadingStatus(true);
      
      // Add timeout for localhost development to prevent hanging
      const isLocalhost = typeof window !== 'undefined' && 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      
      const timeoutMs = isLocalhost ? 3000 : 10000; // 3 seconds for localhost, 10 seconds for production
      
             const withTimeout = (promise: Promise<any>) => {
         return Promise.race([
           promise,
           new Promise<any>((_, reject) => 
             window.setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
           )
         ]);
       };

      const status = await withTimeout(deploymentService.getDeploymentStatus(strategyId));
      console.log('StrategyCard: Raw deployment status received:', status);
      console.log('StrategyCard: Strategy config from deployment:', status?.strategy_config);
      console.log('StrategyCard: Symbols from deployment:', status?.strategy_config?.symbols);
      console.log('StrategyCard: Execution timing from deployment:', status?.strategy_config?.execution_timing);
      console.log('StrategyCard: Risk params from deployment:', status?.strategy_config?.risk_params);
      console.log('StrategyCard: Symbol risk params from deployment:', status?.strategy_config?.symbol_risk_params);
      setDeploymentStatus(status);
      setLastStatusUpdate(new Date());
      
      // If we got strategy config with execution timing, merge it into the strategy object
      if (status?.strategy_config?.executionTiming) {
        console.log('StrategyCard: Merging execution timing from deployment status:', status.strategy_config.executionTiming);
        // Update the strategy object with the execution timing from VM
        // Note: This is a local update and doesn't persist globally, but it will show the correct data
      }
    } catch (error) {
      console.error('StrategyCard: Failed to load deployment status:', error);
      // Strategy might not be deployed, which is fine
      setDeploymentStatus(null);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  // Load deployment status on mount and when strategy changes
  useEffect(() => {
    // Always check deployment status for any strategy - let the service handle the filtering
    if (strategy.id) {
      console.log('StrategyCard: Loading deployment status for strategy:', strategy.id, {
        isActive: strategy.isActive,
        mode: strategy.backtestSettings?.mode,
        name: strategy.name
      });
      loadDeploymentStatus(strategy.id);
    }
  }, [strategy.id, strategy.isActive]);

  // Auto-refresh deployment status for active strategies every 30 seconds
  useEffect(() => {
    if (!deploymentStatus || !strategy.isActive) return;

    const interval = setInterval(() => {
      loadDeploymentStatus(strategy.id!);
    }, 30000);

    return () => clearInterval(interval);
  }, [deploymentStatus, strategy.isActive, strategy.id]);

  // Clear feature data when strategy changes
  useEffect(() => {
    setFeatureData([]);
  }, [strategy.id]);

  // Helper function to determine timeframe display - checks deployment status for execution timing
  const getTimeframeDisplay = (strategy: Strategy): string => {
    // First try to get execution timing from strategy object
    let executionTiming = strategy.executionTiming;
    
    // If not found, try to get it from deployment status (for deployed strategies)
    if (!executionTiming && deploymentStatus?.strategy_config?.executionTiming) {
      executionTiming = deploymentStatus.strategy_config.executionTiming;
      console.log('StrategyCard: Using execution timing from deployment status:', executionTiming);
    }
    
    if (executionTiming) {
      const frequency = executionTiming.signalFrequency;
      const freqMap: { [key: string]: string } = {
        'daily_close': 'Daily',
        'daily_open': 'Daily',
        'hourly': '1H',
        '15min': '15m',
        '5min': '5m',
        '1min': '1m',
        'continuous': 'HFT',
        'event_driven': 'Event'
      };
      return freqMap[frequency] || 'Custom';
    } 
    
    return strategy.duration || '1D';
  };

  // Calculate success rate - prioritize live data over backtest data
  const getSuccessRate = (): string => {
    // TODO: Add live success rate from deployment status when available
    // For now, use backtest data since DeploymentStatus doesn't have performance metrics
    if (strategy.backtestResult?.performance?.winRate) {
      return `${strategy.backtestResult.performance.winRate.toFixed(0)}% (BT)`;
    }
    return 'N/A';
  };

  // Get live performance metrics if available
  const getLivePerformanceMetrics = () => {
    if (!deploymentStatus || !strategy.allocatedCapital) return null;
    
    const allocatedCapital = typeof strategy.allocatedCapital === 'number' 
      ? strategy.allocatedCapital 
      : parseFloat(String(strategy.allocatedCapital) || '0');
    
    if (allocatedCapital <= 0) return null;
    
    const returnPct = (deploymentStatus.total_pnl / allocatedCapital) * 100;
    
    return {
      totalReturn: returnPct,
      totalPnL: deploymentStatus.total_pnl,
      allocatedCapital
    };
  };

  // Get risk level based on strategy settings, deployment config, or backtest results
  const getRiskLevel = (): string => {
    // First priority: explicit risk level from strategy
    if (strategy.riskLevel) {
      return strategy.riskLevel.charAt(0).toUpperCase() + strategy.riskLevel.slice(1);
    }
    
    // Second priority: calculate from risk parameters (strategy or deployment)
    const riskParams = strategy.riskParameters || deploymentStatus?.strategy_config?.riskParameters;
    const deploymentRiskParams = deploymentStatus?.strategy_config?.risk_params;
    
    if (riskParams || deploymentRiskParams) {
      let riskScore = 0;
      let factors = 0;
      
      // Factor 1: Position size risk (higher position size = higher risk)
      const maxPositionPct = riskParams?.maxPositionSizePct || (deploymentRiskParams?.max_position_size && deploymentRiskParams.max_position_size * 100);
      if (maxPositionPct) {
        factors++;
        if (maxPositionPct > 10) riskScore += 2; // High risk: >10% per position
        else if (maxPositionPct > 5) riskScore += 1; // Medium risk: 5-10%
        // Low risk: <=5% (score += 0)
      }
      
      // Factor 2: Stop loss aggressiveness (wider stops = higher risk)
      const stopLossValue = parseFloat(riskParams?.stopLossValue || deploymentRiskParams?.stop_loss || '0');
      if (stopLossValue > 0) {
        factors++;
        if (stopLossValue > 5) riskScore += 0; // Low risk: wide stops >5%
        else if (stopLossValue > 2) riskScore += 1; // Medium risk: 2-5%
        else riskScore += 2; // High risk: tight stops <=2%
      }
      
      // Factor 3: Strategy exposure (higher exposure = higher risk)
      const maxExposure = riskParams?.maxStrategyExposurePct;
      if (maxExposure) {
        factors++;
        if (maxExposure > 90) riskScore += 2; // High risk: >90% exposed
        else if (maxExposure > 70) riskScore += 1; // Medium risk: 70-90%
        // Low risk: <=70% (score += 0)
      }
      
      if (factors > 0) {
        const avgRiskScore = riskScore / factors;
        if (avgRiskScore >= 1.5) return 'High';
        if (avgRiskScore >= 0.5) return 'Medium';
        return 'Low';
      }
    }
    
    // Third priority: calculate from backtest drawdown
    if (strategy.backtestResult?.performance?.maxDrawdown) {
      const drawdown = strategy.backtestResult.performance.maxDrawdown;
      if (drawdown > 15) return 'High';
      if (drawdown > 8) return 'Medium';
      return 'Low';
    }
    
    // Fallback based on deployment status
    if (deploymentStatus?.strategy_config) {
      console.log('StrategyCard: Using deployment-based risk assessment for strategy:', deploymentStatus.strategy_id);
      // If deployed with real money vs paper, assume higher risk tolerance
      return strategy.backtestSettings?.mode === 'live' ? 'Medium' : 'Low';
    }
    
    return 'Medium';
  };

  // Get deployment status info
  const getDeploymentInfo = () => {
    if (!deploymentStatus) return null;
    
    // Calculate symbol count correctly - prefer VM deployment data
    let symbolsCount = 0;
    if (deploymentStatus.strategy_config?.symbols && Array.isArray(deploymentStatus.strategy_config.symbols)) {
      symbolsCount = deploymentStatus.strategy_config.symbols.length;
    } else if (Array.isArray(strategy.symbols)) {
      symbolsCount = strategy.symbols.length;
    } else if (typeof strategy.symbols === 'string' && strategy.symbols) {
      // Handle comma-separated string format
      symbolsCount = strategy.symbols.split(',').filter(s => s.trim()).length;
    }
    
    return {
      status: deploymentStatus.status,
      symbolsCount,
      basketsCount: strategy.baskets?.length || 0,
      totalPnL: deploymentStatus.total_pnl || 0,
      activePositions: deploymentStatus.active_positions || 0,
      lastSignal: deploymentStatus.last_signal_time,
      isPaperTrading: strategy.backtestSettings?.mode === 'paper'
    };
  };

  // Strategy control handlers - now supports pause, resume, and stop
  const handleStrategyControl = async (action: 'pause' | 'resume' | 'stop') => {
    if (!strategy.id || !deploymentStatus) return;

    try {
      switch (action) {
        case 'pause':
          await deploymentService.pauseStrategy(strategy.id);
          toast({
            title: "Strategy Paused",
            description: `"${strategy.name}" has been paused`,
          });
          break;
        case 'resume':  
          await deploymentService.resumeStrategy(strategy.id);
          toast({
            title: "Strategy Resumed", 
            description: `"${strategy.name}" has been resumed`,
          });
          break;
        case 'stop':
          const stopResult = await deploymentService.stopStrategy(strategy.id);
          
          if (stopResult.success) {
            toast({
              title: "Strategy Stopped",
              description: `"${strategy.name}" has been stopped on VM`,
            });
          } else if (stopResult.status === 'endpoint_not_available') {
            toast({
              title: "VM Stop Not Available",
              description: stopResult.message,
              variant: "destructive",
            });
            // Still consider it a "success" for UI purposes since user was warned
          } else {
            throw new Error(stopResult.message || 'Failed to stop strategy');
          }
          break;
      }
      
      // Refresh status after action
      setTimeout(() => loadDeploymentStatus(strategy.id!), 1000);
    } catch (error) {
      toast({
        title: "Action Failed",
        description: `Failed to ${action} strategy: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  // Enhanced delete handler with proper state management
  const handleDeleteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isDeleting) return; // Prevent multiple delete attempts
    
    setIsDeleting(true);
    
    try {
      // If strategy is deployed and running, attempt to delete it completely
      if (deploymentStatus && deploymentStatus.status === 'running') {
        const deleteResult = await deploymentService.deleteStrategy(strategy.id!);
        
        if (deleteResult.success) {
          toast({
            title: "Strategy Deleted Successfully",
            description: `"${strategy.name}" has been completely removed from VM and persistent storage`,
          });
        } else if (deleteResult.status === 'not_found_assumed_deleted') {
          // Strategy not found on VM - probably already deleted
          toast({
            title: "Strategy Removed",
            description: `"${strategy.name}" was not found on VM (may have been already deleted)`,
          });
        } else {
          // Delete failed - warn user but proceed with UI deletion
          toast({
            title: "VM Delete Failed - Removing from UI",
            description: `Could not delete "${strategy.name}" from VM, but removing from UI. Strategy may persist on VM.`,
            variant: "destructive", 
          });
        }
        
        // Give it a moment for the delete operation
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        // Strategy not deployed or not running - try to delete from VM anyway in case it's stored
        try {
          const deleteResult = await deploymentService.deleteStrategy(strategy.id!);
          if (deleteResult.success) {
            toast({
              title: "Strategy Deleted",
              description: `"${strategy.name}" removed from VM storage`,
            });
          }
        } catch (error) {
          // Non-critical error - strategy might not exist on VM
          console.log('Strategy delete attempt (non-critical):', error);
        }
      }
      
      // Proceed with deletion from UI
      onDeleteClick(strategy.id!);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to prepare strategy for deletion",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Function to get feature attributions
  const handleExplainClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowExplain(true);
    
    if (featureData.length === 0) {
      setLoadingExplain(true);
      try {
        const contextData = strategy.backtestResult ? {
          backtestResult: strategy.backtestResult,
          symbols: strategy.symbols,
          models: strategy.models
        } : undefined;
        
        const data = await getFeatureAttributions(strategy.id!, contextData);
        setFeatureData(data);
      } catch (error) {
        console.error("Error fetching feature attributions:", error);
      } finally {
        setLoadingExplain(false);
      }
    }
  };

  // Get status badge for deployment status
  const getStatusBadge = () => {
    if (!deploymentStatus) return null;
    
    const deployInfo = getDeploymentInfo();
    if (!deployInfo) return null;

    const badgeProps = {
      deployed: { variant: "outline" as const, className: "bg-blue-50 text-blue-700 border-blue-200", text: "Deployed" },
      running: { variant: "outline" as const, className: "bg-green-50 text-green-700 border-green-200", text: "Running" },
      paused: { variant: "outline" as const, className: "bg-yellow-50 text-yellow-700 border-yellow-200", text: "Paused" },
      stopped: { variant: "outline" as const, className: "bg-gray-50 text-gray-700 border-gray-200", text: "Stopped" },
      error: { variant: "outline" as const, className: "bg-red-50 text-red-700 border-red-200", text: "Error" },
      manual_ready: { variant: "outline" as const, className: "bg-blue-50 text-blue-700 border-blue-200", text: "Ready" }
    };

    const props = badgeProps[deployInfo.status] || badgeProps.deployed;
    
    return (
      <Badge variant={props.variant} className={props.className}>
        {props.text}
      </Badge>
    );
  };

  const deploymentInfo = getDeploymentInfo();

  return (
    <div
      key={strategy.id}
      className={`p-3 sm:p-4 rounded-[4px] border transition-all duration-200 cursor-pointer relative ${
        selectedStrategy === strategy.id
          ? 'border-blue-500 bg-blue-50'
          : deploymentInfo?.status === 'running'
          ? 'border-green-300 bg-green-50/30'
          : deploymentInfo?.status === 'paused'
          ? 'border-yellow-300 bg-yellow-50/30'
          : 'border-gray-200 hover:border-blue-300'
      } ${isDeleting ? 'opacity-50 pointer-events-none' : ''}`}
      onClick={() => onStrategyClick(strategy)}
    >
      {/* Delete loading overlay */}
      {isDeleting && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded-[4px] z-10">
          <RefreshCw className="w-4 h-4 animate-spin text-red-500" />
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-medium truncate">{strategy.name}</div>
            {strategy.isActive && (
              <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
            )}
            {deploymentInfo && (
              <>
                {isLoadingStatus && (
                  <RefreshCw className="w-3 h-3 animate-spin text-blue-500 flex-shrink-0" />
                )}
                {getStatusBadge()}
                {deploymentInfo.isPaperTrading && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                    <Eye className="w-3 h-3 mr-1" />
                    Paper
                  </Badge>
                )}
              </>
            )}
          </div>
          <div className="text-sm text-gray-500 truncate">{strategy.description}</div>
          {deploymentInfo && (
            <div className="text-xs text-gray-500 mt-1">
              {deploymentInfo.symbolsCount} symbols • {deploymentInfo.basketsCount} baskets
              {deploymentInfo.activePositions > 0 && (
                <> • {deploymentInfo.activePositions} active positions</>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 sm:gap-2 ml-2 flex-shrink-0">
          {isExpanded && (
            <>
              {/* Deployment controls for active strategies - now includes pause/resume */}
              {deploymentInfo && (
                <div className="flex items-center gap-1 mr-2">
                  {deploymentInfo.status === 'running' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStrategyControl('pause');
                      }}
                      className="text-yellow-600 hover:text-yellow-700 p-1"
                      title="Pause Strategy"
                    >
                      <Pause className="w-3 h-3" />
                    </Button>
                  )}
                  
                  {deploymentInfo.status === 'paused' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStrategyControl('resume');
                      }}
                      className="text-green-600 hover:text-green-700 p-1"
                      title="Resume Strategy"
                    >
                      <Play className="w-3 h-3" />
                    </Button>
                  )}
                  
                  {(deploymentInfo.status === 'running' || deploymentInfo.status === 'paused') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStrategyControl('stop');
                      }}
                      className="text-red-600 hover:text-red-700 p-1"
                      title="Stop Strategy"
                    >
                      <Square className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onResearch(strategy.id!);
                }}
                className="text-gray-500 hover:text-blue-600 p-1 sm:p-2"
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDeleteClick}
                className="text-gray-500 hover:text-red-600 p-1 sm:p-2"
                disabled={isDeleting}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>
      
      {isExpanded && (
        <>
          {/* Live Deployment Status - Most Important Info First */}
          {deploymentInfo && (
            <div className="mt-3 pt-3 border-t bg-green-50/50 -mx-3 sm:-mx-4 px-3 sm:px-4 py-3 rounded">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-green-800 flex items-center gap-1">
                  <Activity className="w-3 h-3" />
                  Live Strategy Status
                </div>
                <div className="text-xs text-green-600">
                  Last Update: {lastStatusUpdate?.toLocaleTimeString() || 'Unknown'}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="font-medium text-green-700 capitalize">{deploymentInfo.status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Trading Mode:</span>
                      <span className="font-medium">{deploymentInfo.isPaperTrading ? 'Paper' : 'Live'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Active Symbols:</span>
                      <span className="font-medium text-blue-600">
                        {(() => {
                          // Use VM deployment symbols for accurate count
                          if (deploymentStatus?.strategy_config?.symbols && Array.isArray(deploymentStatus.strategy_config.symbols)) {
                            return deploymentStatus.strategy_config.symbols.length;
                          }
                          return deploymentInfo.symbolsCount || 0;
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Positions:</span>
                      <span className="font-medium">{deploymentInfo.activePositions}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total P&L:</span>
                      <span className={`font-medium ${deploymentInfo.totalPnL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {deploymentInfo.totalPnL >= 0 ? '+' : ''}${deploymentInfo.totalPnL.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Deployed:</span>
                      <span className="font-medium text-xs">
                        {deploymentStatus?.deployment_time ? 
                          new Date(deploymentStatus.deployment_time).toLocaleString() : 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Show actual symbols being traded */}
              {deploymentStatus?.strategy_config?.symbols && Array.isArray(deploymentStatus.strategy_config.symbols) && (
                <div className="mt-2 pt-2 border-t border-green-200">
                  <div className="text-xs text-gray-600 mb-1">Trading Symbols:</div>
                  <div className="flex flex-wrap gap-1">
                    {deploymentStatus.strategy_config.symbols.map((symbol, idx) => (
                      <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {symbol}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Strategy Configuration - What Was Actually Deployed */}
          <div className="mt-3 pt-3 border-t bg-blue-50/30 -mx-3 sm:-mx-4 px-3 sm:px-4 py-3 rounded">
            <div className="text-xs font-semibold text-blue-800 mb-2 flex items-center gap-1">
              <Settings className="w-3 h-3" />
              Deployed Configuration
              {!deploymentStatus && (
                <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">
                  Local
                </span>
              )}
            </div>
            
            {(() => {
              // Debug: Log what we actually have
              if (process.env.NODE_ENV === 'development') {
                console.log('StrategyCard Debug - deploymentStatus:', deploymentStatus);
                console.log('StrategyCard Debug - strategy:', strategy);
              }
              
              // Prioritize actual deployment config, fallback to strategy data
              const config = deploymentStatus?.strategy_config || {};
              const execTiming = config.execution_timing || config.executionTiming || strategy.executionTiming || {};
              const models = config.models || strategy.models || [];
              const engine = config.engine || strategy.engine;
              const symbols = config.symbols || strategy.symbols || [];
              
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  <div>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Engine Type:</span>
                        <span className="font-medium capitalize">
                          {(() => {
                            if (engine && engine !== 'default') {
                              return engine === 'stewie_quant' ? 'StewieQuant' : engine;
                            }
                            if (models.length > 0) {
                              return `${models[0].type || 'Mixed'} Engine`;
                            }
                            return 'Static';
                          })()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">AI Models:</span>
                        <span className="font-medium">
                          {(() => {
                            if (models.length > 0) {
                              if (models.length === 1) {
                                return models[0].type ? models[0].type.toUpperCase() : 'Custom';
                              }
                              return `${models.length} Models`;
                            }
                            return 'Static Only';
                          })()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Symbols:</span>
                        <span className="font-medium">
                          {(() => {
                            const symbolCount = Array.isArray(symbols) ? symbols.length : 
                                              (typeof symbols === 'string' ? symbols.split(',').length : 0);
                            return symbolCount > 0 ? `${symbolCount} Assets` : 'Not Set';
                          })()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Capital:</span>
                        <span className="font-medium text-green-600">
                          ${(() => {
                            // Prioritize VM deployment data, fallback to strategy data
                            const vmCapital = config.allocated_capital || config.allocatedCapital;
                            const strategyCapital = strategy.allocatedCapital;
                            const capital = vmCapital || strategyCapital;
                            return capital ? Number(capital).toLocaleString() : 'Not Set';
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Signal Frequency:</span>
                        <span className="font-medium">
                          {execTiming.signal_frequency || execTiming.signalFrequency || 'Not Set'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Monitor Frequency:</span>
                        <span className="font-medium">
                          {execTiming.monitor_frequency || execTiming.monitorFrequency || 'Not Set'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Trading Hours:</span>
                        <span className="font-medium">
                          {(() => {
                            if (execTiming.execute_rth_only === false || execTiming.executeRthOnly === false) {
                              return 'Extended Hours';
                            }
                            if (execTiming.execute_rth_only === true || execTiming.executeRthOnly === true) {
                              return 'RTH Only';
                            }
                            return 'Not Set';
                          })()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Mode:</span>
                        <span className="font-medium">
                          {deploymentStatus?.strategy_config?.trading_mode || 
                           (deploymentStatus ? 'Live' : 'Paper')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
            
            {/* Show data source indicator */}
            <div className="mt-3 pt-2 border-t border-blue-200">
              <div className="text-blue-600 text-xs flex items-center justify-between">
                <span>
                  {deploymentStatus ? 
                    '✓ Data from VM deployment' : 
                    '⚠ Using local strategy data'
                  }
                </span>
                {deploymentStatus?.last_signal_time && (
                  <span className="text-blue-500">
                    Last signal: {new Date(deploymentStatus.last_signal_time).toLocaleTimeString()}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Risk Management Settings - Redesigned for Multi-Symbol Strategies */}
          <div className="mt-3 pt-3 border-t bg-orange-50/30 -mx-3 sm:-mx-4 px-3 sm:px-4 py-3 rounded">
            <div className="text-xs font-semibold text-orange-800 mb-2 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Risk Management Overview
            </div>
            
            {(() => {
              const riskParams = deploymentStatus?.strategy_config?.risk_params || 
                               (strategy as any).riskManagement || 
                               strategy.riskParameters;
              const symbols = deploymentStatus?.strategy_config?.symbols || strategy.symbols || [];
              const symbolRiskParams = deploymentStatus?.strategy_config?.symbol_risk_params || [];
              const baskets = strategy.baskets || [];
              const symbolCount = Array.isArray(symbols) ? symbols.length : (typeof symbols === 'string' ? symbols.split(',').length : 0);
              
              // Check if we have complex risk structure (multiple symbols/baskets with different risk params)
              const hasComplexRisk = symbolCount > 1 || baskets.length > 0;
              
              if (hasComplexRisk) {
                // Show aggregated risk overview for multi-symbol strategies
                return (
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Strategy Type:</span>
                      <span className="font-medium">
                        {symbolCount > 1 ? `Multi-Symbol (${symbolCount} assets)` : 'Single Asset'}
                      </span>
                    </div>
                    
                    {baskets.length > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Baskets:</span>
                        <span className="font-medium">{baskets.length} configured</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Risk Management:</span>
                      <span className="font-medium text-orange-600">
                        {riskParams?.max_position_size || riskParams?.stop_loss || riskParams?.profit_target 
                          ? 'Active' : 'Strategy-Level Only'}
                      </span>
                    </div>
                    
                    {strategy.allocatedCapital && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Total Capital:</span>
                        <span className="font-medium text-green-600">
                          ${Number(strategy.allocatedCapital).toLocaleString()}
                        </span>
                      </div>
                    )}
                    
                    <div className="mt-2 pt-2 border-t border-orange-200">
                      {symbolRiskParams && symbolRiskParams.length > 0 ? (
                        <div className="space-y-1">
                          <div className="text-orange-700 text-xs font-medium mb-1">
                            Symbol-Specific Risk Parameters:
                          </div>
                          {symbolRiskParams.slice(0, 3).map((param: any, index: number) => (
                            <div key={index} className="flex justify-between items-center text-xs">
                              <span className="font-medium text-blue-600">{param.symbol}:</span>
                              <span className="text-gray-700">
                                {param.profit_target}% profit / {param.stop_loss}% stop
                              </span>
                            </div>
                          ))}
                          {symbolRiskParams.length > 3 && (
                            <div className="text-orange-600 text-xs">
                              +{symbolRiskParams.length - 3} more symbols...
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-orange-700 text-xs">
                          💡 Risk parameters vary by symbol/basket. View details in strategy settings.
                        </div>
                      )}
                    </div>
                  </div>
                );
              } else {
                // Show traditional single-value display for simple strategies
                return (
                  <div className="grid grid-cols-3 gap-4 text-xs">
                    <div className="text-center">
                      <div className="text-gray-600 mb-1">Max Position</div>
                      <div className="font-medium text-lg">
                        {(() => {
                          const maxPos = riskParams?.max_position_size || riskParams?.maxPositionSizePct;
                          if (maxPos) {
                            const pct = typeof maxPos === 'number' && maxPos <= 1 ? maxPos * 100 : maxPos;
                            return `${parseFloat(String(pct)).toFixed(1)}%`;
                          }
                          return 'N/A';
                        })()}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-gray-600 mb-1">Stop Loss</div>
                      <div className="font-medium text-lg text-red-600">
                        {(() => {
                          const stopLoss = riskParams?.stop_loss || riskParams?.stopLossValue;
                          return stopLoss ? `${parseFloat(String(stopLoss)).toFixed(1)}%` : 'N/A';
                        })()}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-gray-600 mb-1">Take Profit</div>
                      <div className="font-medium text-lg text-green-600">
                        {(() => {
                          const profit = riskParams?.profit_target || riskParams?.profitTargetValue;
                          return profit ? `${parseFloat(String(profit)).toFixed(1)}%` : 'N/A';
                        })()}
                      </div>
                    </div>
                  </div>
                );
              }
            })()}
          </div>

          {/* Performance Metrics (if available) */}
          {(() => {
            const liveMetrics = getLivePerformanceMetrics();
            return liveMetrics ? (
              <div className="mt-3 pt-3 border-t bg-purple-50/30 -mx-3 sm:-mx-4 px-3 sm:px-4 py-3 rounded">
                <div className="text-xs font-semibold text-purple-800 mb-2 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  Live Performance
                </div>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Return:</span>
                    <span className={`font-medium ${liveMetrics.totalReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {liveMetrics.totalReturn >= 0 ? '+' : ''}{liveMetrics.totalReturn.toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Capital Used:</span>
                    <span className="font-medium">
                      ${liveMetrics.allocatedCapital.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            ) : null;
          })()}

          {/* Backtest Results (if available) */}
          {strategy.backtestResult?.performance && (
            <div className="mt-3 pt-3 border-t bg-gray-50/50 -mx-3 sm:-mx-4 px-3 sm:px-4 py-3 rounded">
              <div className="text-xs font-semibold text-gray-700 mb-2">Backtest Performance (Historical)</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div className="text-center">
                  <div className="text-gray-600 mb-1">Total Return</div>
                  <div className={`font-medium ${strategy.backtestResult.performance.totalReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {strategy.backtestResult.performance.totalReturn.toFixed(1)}%
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-gray-600 mb-1">Sharpe Ratio</div>
                  <div className="font-medium">
                    {strategy.backtestResult.performance.sharpeRatio.toFixed(2)}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-gray-600 mb-1">Max Drawdown</div>
                  <div className="font-medium text-red-600">
                    {strategy.backtestResult.performance.maxDrawdown.toFixed(1)}%
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-gray-600 mb-1">Win Rate</div>
                  <div className="font-medium">
                    {strategy.backtestResult.performance.winRate.toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-3 pt-3 border-t flex justify-between items-center">
            <div className="text-xs text-gray-500">
              Strategy ID: {strategy.id?.slice(-8) || 'Unknown'}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExplainClick}
              className="text-gray-500 hover:text-blue-600"
              disabled={loadingExplain}
            >
              {loadingExplain ? "Loading..." : "Explain Prediction"}
            </Button>
          </div>
        </>
      )}

      {/* Explainability Dialog */}
      <Dialog open={showExplain} onOpenChange={setShowExplain}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Feature Importance</DialogTitle>
          </DialogHeader>
          {loadingExplain ? (
            <div className="p-4 text-center">Loading feature attributions...</div>
          ) : (
            <FeatureImportance data={featureData} />
          )}
          <div className="mt-4 flex justify-end">
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
